import React, { useEffect, useMemo, useRef, useState } from 'react'
import { GeoJSON, MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet'
import CircularProgress from '@mui/material/CircularProgress'
import Button from '../UI/Button'
import 'leaflet/dist/leaflet.css'

const DEFAULT_MAP_CENTER = [50.8273907, 3.2631452]
const MAP_LIMIT = 500
const MIN_FETCH_ZOOM = 12

const getFeatureKey = (feature) => (
  feature?.properties?.capa_key ||
  feature?.properties?.capakey ||
  feature?.properties?.project_uuids?.join('|') ||
  JSON.stringify(feature?.geometry?.coordinates?.[0]?.[0] || feature?.geometry?.coordinates?.[0] || '')
)

const buildFeatureCollection = (features) => ({
  type: 'FeatureCollection',
  features,
})

const getBoundsKey = (bounds) => {
  if (!bounds) return ''
  const west = bounds.getWest().toFixed(5)
  const south = bounds.getSouth().toFixed(5)
  const east = bounds.getEast().toFixed(5)
  const north = bounds.getNorth().toFixed(5)
  return `${west},${south},${east},${north}`
}

const getSimplifyMeters = (zoom) => {
  if (zoom < 12) return 12
  if (zoom < 14) return 4
  if (zoom < 16) return 2
  return 0
}

const formatLoadedCount = ({ loadedCount, totalCount, hasMore }) => {
  if (typeof totalCount === 'number') {
    return `${loadedCount.toLocaleString()} of ${totalCount.toLocaleString()} loaded`
  }

  if (hasMore) {
    return `${loadedCount.toLocaleString()}+ loaded`
  }

  return `${loadedCount.toLocaleString()} of ${loadedCount.toLocaleString()} loaded`
}

function MapMoveHandler({ onViewportChange }) {
  const map = useMapEvents({
    moveend: () => onViewportChange(map),
    zoomend: () => onViewportChange(map),
  })

  useEffect(() => {
    onViewportChange(map)
  }, [map, onViewportChange])

  return null
}

function MapResizeHandler({ active }) {
  const map = useMap()

  useEffect(() => {
    if (!active) return
    const timeout = window.setTimeout(() => {
      map.invalidateSize()
    }, 120)

    return () => window.clearTimeout(timeout)
  }, [active, map])

  return null
}

function MapCenterHandler({ centerRequest }) {
  const map = useMap()

  useEffect(() => {
    if (!centerRequest?.center) return
    map.flyTo(centerRequest.center, centerRequest.zoom || 16, { duration: 0.75 })
  }, [centerRequest, map])

  return null
}

function PropertiesMapView({ active, centerRequest, onOpenProperty }) {
  const debounceRef = useRef(null)
  const requestRef = useRef(0)
  const [bbox, setBbox] = useState('')
  const [zoom, setZoom] = useState(16)
  const [features, setFeatures] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [meta, setMeta] = useState({
    batchCount: 0,
    hasMore: false,
    nextOffset: 0,
    totalCount: null,
    truncated: false,
  })

  const featureCollection = useMemo(() => buildFeatureCollection(features), [features])

  const fetchParcels = async ({ nextBbox, nextZoom, offset = 0, append = false }) => {
    if (nextZoom < MIN_FETCH_ZOOM) {
      setFeatures([])
      setMeta({
        batchCount: 0,
        hasMore: false,
        nextOffset: 0,
        totalCount: null,
        truncated: false,
      })
      setErrorMessage('Zoom in to load parcel overlays.')
      return
    }

    const requestId = requestRef.current + 1
    requestRef.current = requestId
    setErrorMessage('')
    if (append) {
      setLoadingMore(true)
    } else {
      setLoading(true)
    }

    const query = new URLSearchParams({
      bbox: nextBbox,
      limit: String(MAP_LIMIT),
      offset: String(offset),
      simplify_meters: String(getSimplifyMeters(nextZoom)),
    })

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/properties/map-capa-polygons?${query.toString()}`, {
        credentials: 'include',
      })

      if (!response.ok) {
        const payload = await response.json().catch(() => null)
        throw new Error(payload?.message || `Map lookup failed with status ${response.status}`)
      }

      const data = await response.json()
      if (requestRef.current !== requestId) return

      const incomingFeatures = Array.isArray(data?.features) ? data.features : []
      const nextFeatures = append
        ? Array.from(new Map([...features, ...incomingFeatures].map((feature) => [getFeatureKey(feature), feature])).values())
        : incomingFeatures
      const returnedCount = typeof data?.returned_count === 'number' ? data.returned_count : incomingFeatures.length
      const hasMore = Boolean(data?.has_more ?? data?.truncated)

      setFeatures(nextFeatures)
      setMeta({
        batchCount: append ? meta.batchCount + 1 : 1,
        hasMore,
        nextOffset: typeof data?.next_offset === 'number' ? data.next_offset : offset + returnedCount,
        totalCount: typeof data?.total_count === 'number' ? data.total_count : null,
        truncated: Boolean(data?.truncated),
      })
    } catch (error) {
      if (requestRef.current === requestId) {
        setErrorMessage(error.message || 'Map lookup unavailable')
      }
    } finally {
      if (requestRef.current === requestId) {
        setLoading(false)
        setLoadingMore(false)
      }
    }
  }

  const handleViewportChange = (map) => {
    const nextBbox = getBoundsKey(map.getBounds())
    const nextZoom = map.getZoom()
    if (!nextBbox || nextBbox === bbox) return

    window.clearTimeout(debounceRef.current)
    debounceRef.current = window.setTimeout(() => {
      setBbox(nextBbox)
      setZoom(nextZoom)
      setFeatures([])
      setMeta({
        batchCount: 0,
        hasMore: false,
        nextOffset: 0,
        totalCount: null,
        truncated: false,
      })
      fetchParcels({ nextBbox, nextZoom, offset: 0, append: false })
    }, 400)
  }

  useEffect(() => () => window.clearTimeout(debounceRef.current), [])

  const handleLoadMore = () => {
    if (!bbox || loading || loadingMore) return
    fetchParcels({ nextBbox: bbox, nextZoom: zoom, offset: meta.nextOffset, append: true })
  }

  const geoJsonStyle = () => ({
    color: '#04434c',
    weight: 1.4,
    opacity: 0.9,
    fillColor: '#1490a3',
    fillOpacity: 0.34,
  })

  const onEachFeature = (feature, layer) => {
    const properties = feature?.properties || {}
    const capaKey = properties.capa_key || properties.capakey || ''
    const projectNumbers = Array.isArray(properties.projectnummers) ? properties.projectnummers.filter(Boolean) : []
    const projectCount = properties.project_count ?? projectNumbers.length
    const popupElement = document.createElement('div')
    popupElement.className = 'properties-map-popup'

    const capakeyElement = document.createElement('div')
    capakeyElement.className = 'properties-map-popup__capakey'
    capakeyElement.textContent = capaKey || 'Unknown capakey'
    popupElement.appendChild(capakeyElement)

    const metaElement = document.createElement('div')
    metaElement.className = 'properties-map-popup__meta'
    metaElement.textContent = `${projectCount || 0} project${projectCount === 1 ? '' : 's'}`
    popupElement.appendChild(metaElement)

    if (projectNumbers.length > 0) {
      const projectsElement = document.createElement('div')
      projectsElement.className = 'properties-map-popup__projects'
      projectsElement.textContent = `${projectNumbers.slice(0, 3).join(', ')}${projectNumbers.length > 3 ? '...' : ''}`
      popupElement.appendChild(projectsElement)
    }

    const buttonElement = document.createElement('button')
    buttonElement.type = 'button'
    buttonElement.className = 'properties-map-popup__button'
    buttonElement.textContent = 'Open property'
    buttonElement.addEventListener('click', () => {
      if (capaKey) {
        onOpenProperty(capaKey)
      }
    })
    popupElement.appendChild(buttonElement)

    layer.bindPopup(popupElement)
  }

  const loadedCountLabel = formatLoadedCount({
    loadedCount: features.length,
    totalCount: meta.totalCount,
    batchCount: Math.max(meta.batchCount, features.length > 0 ? 1 : 0),
    hasMore: meta.hasMore,
  })

  return (
    <div className='properties-map-view'>
      <div className='properties-map-view__map-shell'>
        <MapContainer center={DEFAULT_MAP_CENTER} zoom={16} minZoom={8} maxZoom={20} className='properties-map-view__map'>
          <MapResizeHandler active={active} />
          <MapCenterHandler centerRequest={centerRequest} />
          <MapMoveHandler onViewportChange={handleViewportChange} />
          <TileLayer
            attribution='&copy; OpenStreetMap contributors &copy; CARTO'
            url='https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
          />
          {features.length > 0 && (
            <GeoJSON
              key={`${bbox}-${features.length}`}
              data={featureCollection}
              style={geoJsonStyle}
              onEachFeature={onEachFeature}
            />
          )}
        </MapContainer>

        {loading && (
          <div className='properties-map-view__loading'>
            <CircularProgress color='inherit' />
          </div>
        )}
      </div>

      <div className='properties-map-view__footer'>
        <div>
          <div className='properties-map-view__count'>{loadedCountLabel}</div>
          {meta.truncated && <div className='properties-map-view__hint'>This area has more parcels. Load more or zoom in for a smaller selection.</div>}
          {errorMessage && <div className='properties-map-view__error'>{errorMessage}</div>}
        </div>
        <Button
          variant='secondary'
          size='sm'
          shape='pill'
          disabled={!meta.hasMore || loading || loadingMore}
          onClick={handleLoadMore}
          leftIcon={loadingMore ? <CircularProgress size={14} thickness={5} color='inherit' /> : null}
        >
          {loadingMore ? 'Loading...' : 'Load more'}
        </Button>
      </div>
    </div>
  )
}

export default PropertiesMapView
